import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DemoAction } from './actions.ts';
import { fetchPreferredDemoSnapshot, getConfiguredDemoDataMode, getInitialDemoSnapshot, getResolvedDemoDataMode, runPreferredDemoAction } from './demoApi.ts';
import { MVP_QUERY_KEY_ROOT } from './useMvpData.ts';

export const DEMO_SNAPSHOT_QUERY_KEY = ['field-service-technique-demo-snapshot'];

export function useDemoData() {
  const queryClient = useQueryClient();
  const initialSnapshot = getInitialDemoSnapshot();

  const snapshotQuery = useQuery({
    queryKey: DEMO_SNAPSHOT_QUERY_KEY,
    queryFn: ({ signal }) => fetchPreferredDemoSnapshot(signal),
    initialData: initialSnapshot,
    staleTime: Infinity,
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: async (action: DemoAction) => runPreferredDemoAction(action),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: DEMO_SNAPSHOT_QUERY_KEY });
    },
    onSuccess: async (snapshot) => {
      queryClient.setQueryData(DEMO_SNAPSHOT_QUERY_KEY, snapshot);
      await queryClient.invalidateQueries({ queryKey: MVP_QUERY_KEY_ROOT });
      await queryClient.refetchQueries({ queryKey: MVP_QUERY_KEY_ROOT, type: 'active' });
    },
  });

  async function refreshSnapshot() {
    await queryClient.invalidateQueries({ queryKey: DEMO_SNAPSHOT_QUERY_KEY });
    await queryClient.refetchQueries({ queryKey: DEMO_SNAPSHOT_QUERY_KEY, type: 'active' });
  }

  return {
    snapshot: snapshotQuery.data ?? initialSnapshot,
    isLoading: snapshotQuery.isLoading,
    isPending: mutation.isPending,
    configuredMode: getConfiguredDemoDataMode(),
    resolvedMode: getResolvedDemoDataMode(),
    runAction: mutation.mutateAsync,
    refreshSnapshot,
  };
}
